/* @sravni/react-header version 0.2.43 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useBoolean, useAuthLoginLink, safeInvoke, useBrowserThemeDetector, preventBodyScroll } from '@sravni/react-utils';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Logo } from '@sravni/react-design-system/lib/Logo';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Layout } from '@sravni/react-design-system/lib/Layout';
import { Link } from '@sravni/react-design-system/lib/Typography';
import { Divider } from '@sravni/react-design-system';
import { subscribeOnLoginSuccess } from '@sravni/utils/lib/auth';
import { decorateSearchUrlByLinksMap, generateSearchUrl } from '../../../utils/search.js';
import { linksMap, domainsDefault } from '../../../constants/links.js';
import { getModeView } from '../../../utils/modes.js';
import { generateUrl } from '../../../utils/links.js';
import { useAnalyticEvents, EVENTS } from '../../../hooks/useAnalyticEvents.js';
import MobileNavigation from './MobileNavigation.js';
import MobileUserBlock from './MobileUserBlock.js';
import '../../ThemeSwitcher/index.js';
import modules_8de2575d from '../styles/MobileHeader.module.less.js';
import ThemeSwitcher from '../../ThemeSwitcher/src/ThemeSwitcher.js';

/**
 * @description Mobile header component.
 *
 * @component
 * @example
 * ```jsx
 * <MobileHeader user={user} settings={settings} />
 * ```
 */
var MobileHeader = React.forwardRef(function (props, ref) {
    var serviceURL = props.serviceURL, _a = props.settings, settings = _a === void 0 ? {} : _a, user = props.user, regions = props.regions, oidcHandlerPrefix = props.oidcHandlerPrefix, currentRegion = props.currentRegion, fixedBackground = props.fixedBackground, promo = props.promo, _b = props.mode, mode = _b === void 0 ? 'default' : _b, _c = props.enableTheme, enableTheme = _c === void 0 ? false : _c, className = props.className, domainsOutside = props.domains, linksMapOutside = props.linksMap, formatPath = props.formatPath, onOpen = props.onOpen, onClose = props.onClose, restProps = __rest(props, ["serviceURL", "settings", "user", "regions", "oidcHandlerPrefix", "currentRegion", "fixedBackground", "promo", "mode", "enableTheme", "className", "domains", "linksMap", "formatPath", "onOpen", "onClose"]);
    var cx = useStyles(modules_8de2575d);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _d = __read(useBoolean(false), 2), visibleNavigationMenu = _d[0], setVisibleNavigationMenu = _d[1];
    var _e = __read(useBoolean(false), 2), visibleUserMenu = _e[0], setVisibleUserMenu = _e[1];
    var authLoginLinkProps = useAuthLoginLink({ prefix: oidcHandlerPrefix, isNewWindow: true });
    var container = cx(className, 'container', {
        active: fixedBackground || visibleNavigationMenu || visibleUserMenu
    });
    var modeView = React.useMemo(function () { return getModeView(mode); }, [mode]);
    var linksMap$1 = React.useMemo(function () { return (__assign(__assign({}, linksMap), linksMapOutside)); }, [linksMapOutside]);
    var domains = React.useMemo(function () { return (__assign(__assign({}, domainsDefault), domainsOutside)); }, [domainsOutside]);
    var searchUrl = React.useMemo(function () { return decorateSearchUrlByLinksMap(generateSearchUrl(domains), linksMap$1); }, [domains, linksMap$1]);
    var handleOpenNavigationMenu = React.useCallback(function () {
        setVisibleNavigationMenu.on();
        setVisibleUserMenu.off();
        safeInvoke(onOpen);
        pushAnalyticEvent(EVENTS.MOBILE_BURGER_CLICK, { eventLabel: "\u041E\u0442\u043A\u0440\u044B\u0442\u0438\u0435" });
        pushAnalyticEvent(EVENTS.DOWNLOAD_APP, { eventAction: 'Показ ссылки в Store', eventLabel: "\u041C\u0435\u043D\u044E \u043F\u0440\u043E\u0444\u0438\u043B\u044F|\u041E\u0431\u0449\u0430\u044F \u043A\u043D\u043E\u043F\u043A\u0430" });
    }, []);
    var handleCloseNavigationMenu = React.useCallback(function () {
        setVisibleNavigationMenu.off();
        safeInvoke(onClose);
        pushAnalyticEvent(EVENTS.MOBILE_BURGER_CLICK, { eventLabel: "\u0417\u0430\u043A\u0440\u044B\u0442\u0438\u0435" });
    }, []);
    var handleOpenUserMenu = React.useCallback(function () {
        setVisibleUserMenu.on();
        setVisibleNavigationMenu.off();
        pushAnalyticEvent(EVENTS.USER_MENU_SHOW, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|\u041A\u043B\u0438\u043A" });
    }, []);
    var handleCloseUserMenu = React.useCallback(function () {
        setVisibleUserMenu.off();
    }, []);
    var handleAuthLoginClick = React.useCallback(function () {
        var callback = function () { return window.location.reload(); };
        subscribeOnLoginSuccess(callback);
        pushAnalyticEvent(EVENTS.LOGIN);
    }, []);
    var handleThemeSwitcherClick = React.useCallback(function (currentTheme) {
        var _a, _b;
        (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.sravniDataLayer) === null || _b === void 0 ? void 0 : _b.push({ theme: currentTheme });
        pushAnalyticEvent(EVENTS.THEME_SWITCHER_CLICK, { eventLabel: "\u0422\u0435\u043C\u0430 - " + currentTheme });
    }, [pushAnalyticEvent]);
    var handleBrowserThemeDetectorChange = React.useCallback(function (currentTheme) {
        var _a, _b;
        (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.sravniDataLayer) === null || _b === void 0 ? void 0 : _b.push({ theme: currentTheme });
        pushAnalyticEvent(EVENTS.THEME_DETECTOR_CHANGE, { eventLabel: "\u0422\u0435\u043C\u0430 - " + currentTheme });
    }, [pushAnalyticEvent]);
    useBrowserThemeDetector({ enabled: enableTheme, onChange: handleBrowserThemeDetectorChange });
    var logo = React.useMemo(function () { return (React.createElement("a", { "data-element": "logo-link", href: generateUrl('/', domains.base), className: cx('logo') },
        React.createElement(Logo, null))); }, [domains.base]);
    React.useEffect(function () {
        if (visibleNavigationMenu) {
            preventBodyScroll(true);
        }
        else {
            preventBodyScroll();
        }
        return function () {
            return preventBodyScroll();
        };
    }, [visibleNavigationMenu]);
    var mobileUserBlock = React.useMemo(function () {
        if (user) {
            return (React.createElement(MobileUserBlock, { serviceURL: serviceURL, formatPath: formatPath, regions: regions, user: user, settings: settings, currentRegion: currentRegion, domains: domains, 
                //linksMap={linksMap}
                oidcHandlerPrefix: oidcHandlerPrefix, visible: visibleUserMenu, onOpen: handleOpenUserMenu, onClose: handleCloseUserMenu }));
        }
        return (React.createElement(Link, __assign({}, authLoginLinkProps, { onClickCapture: handleAuthLoginClick }),
            React.createElement(Button, { size: 36, variant: fixedBackground ? "secondary" : "outlined", color: "gray" }, "\u0412\u043E\u0439\u0442\u0438")));
    }, [
        user,
        currentRegion,
        regions,
        serviceURL,
        settings,
        oidcHandlerPrefix,
        linksMap$1,
        domains,
        visibleUserMenu,
        authLoginLinkProps,
        fixedBackground,
        formatPath,
        handleAuthLoginClick,
    ]);
    var mobileNav = React.useMemo(function () {
        return (React.createElement(React.Fragment, null,
            React.createElement(Space, { size: 12, inline: true },
                modeView.showNavigate && (React.createElement(MobileNavigation, { settings: settings, promo: promo, showSearch: modeView.showSearch, visible: visibleNavigationMenu, searchUrl: searchUrl, onOpen: handleOpenNavigationMenu, onClose: handleCloseNavigationMenu })),
                modeView.showLogo && logo),
            React.createElement(Space, { size: 12, inline: true },
                enableTheme && React.createElement(ThemeSwitcher, { onClick: handleThemeSwitcherClick }),
                modeView.showUser && (React.createElement(Space, { size: 12, align: "start", inline: true },
                    React.createElement(Space, { size: 32, align: "center" }, mobileUserBlock))))));
    }, [user, modeView, searchUrl, enableTheme, mobileUserBlock, visibleNavigationMenu, promo, settings, handleThemeSwitcherClick]);
    return (React.createElement(React.Fragment, null,
        React.createElement("header", __assign({ ref: ref, "data-qa": "Header", className: container }, restProps),
            React.createElement(Layout, null,
                React.createElement(Space, { justify: "space-between", align: "center" }, mobileNav))),
        React.createElement(Divider, null)));
});
MobileHeader.defaultProps = {
    mode: 'default',
    fixedBackground: false,
    domains: domainsDefault,
    enableTheme: false
};
MobileHeader.displayName = 'MobileHeader';

export { MobileHeader as default };
//# sourceMappingURL=MobileHeader.js.map
